/// <reference path="../../nativewind-augmentation.d.ts" />
// Copyright (c) Ping. All rights reserved.
//
// Default tab bar for `TabView` — equal-width pressable tabs with an animated
// underline indicator that tracks the live pager offset (so it follows a swipe
// in real time, not just on settle). Pure RN core; works on web + macOS.

import { Animated, Pressable, Text, View } from 'react-native';
import type { TabBarRenderProps } from './TabView';

export function TabBar({
  routes,
  index,
  onIndexChange,
  position,
  layoutWidth,
}: TabBarRenderProps): JSX.Element {
  const count = routes.length;
  const tabWidth = count > 0 ? layoutWidth / count : 0;

  // Map the pager offset (0 → -(count-1)*width) onto the indicator's x
  // (0 → (count-1)*tabWidth). Guard the degenerate pre-layout state.
  const indicatorTranslate =
    layoutWidth > 0 && count > 1
      ? position.interpolate({
          inputRange: [-(count - 1) * layoutWidth, 0],
          outputRange: [(count - 1) * tabWidth, 0],
          extrapolate: 'clamp',
        })
      : 0;

  return (
    <View className="relative flex-row border-b border-light-border-subtle dark:border-dark-border-subtle">
      {routes.map((route, i) => {
        const focused = i === index;
        return (
          <Pressable
            key={route.key}
            accessibilityRole="tab"
            accessibilityState={{ selected: focused }}
            onPress={() => onIndexChange(i)}
            className="flex-1 h-10 flex-row items-center justify-center gap-1.5 active:opacity-80"
          >
            <Text
              numberOfLines={1}
              className={`text-[14px] leading-[20px] ${
                focused
                  ? 'font-semibold text-light-accent-brand dark:text-[#537EFF]'
                  : 'font-medium text-light-text-secondary dark:text-dark-text-secondary'
              }`}
            >
              {route.title ?? route.key}
            </Text>
            {route.badge !== undefined && route.badge > 0 ? (
              <View className="min-w-[16px] h-[16px] px-1 rounded-full items-center justify-center bg-light-accent-brand dark:bg-[#537EFF]">
                <Text className="text-[10px] leading-[12px] font-bold text-white">
                  {route.badge > 99 ? '99+' : route.badge}
                </Text>
              </View>
            ) : null}
          </Pressable>
        );
      })}
      {/* Animated underline indicator (only meaningful once measured). */}
      {layoutWidth > 0 && count > 1 ? (
        <Animated.View
          className="absolute bottom-0 h-[2px] rounded-full bg-light-accent-brand dark:bg-[#537EFF]"
          style={{ width: tabWidth, transform: [{ translateX: indicatorTranslate }] }}
        />
      ) : null}
    </View>
  );
}
